/**
 * HistoryPersistenceBridge — монтируется на уровне `AppProvider`.
 *
 * Слушает `globalGen.status` и, когда генерация завершается в фоне
 * (пользователь мог быть на любой странице — /files, /account, /tariffs),
 * подхватывает результат через backend API и сохраняет в историю
 * через `useRecentGenerationsContext.addGeneration`.
 *
 * Это нужно для случаев когда handleGenerate-промис уже отработал
 * ИЛИ был прерван (reload страницы). Если handleGenerate сохранил сам —
 * bridge увидит что pending-snapshot пустой и ничего не сделает (idempotent).
 *
 * Этот компонент не имеет UI (return null).
 */
import { useEffect, useRef } from 'react'
import { useGlobalGeneration } from './GlobalGenerationContext'
import { useRecentGenerationsContext } from './RecentGenerationsContext'
import { getTaskStatus } from '../api/images'
import {
	clearPendingSnapshot,
	loadPendingSnapshot,
	saveToHistory,
} from './historyPersistence'

export default function HistoryPersistenceBridge() {
	const { status } = useGlobalGeneration()
	const { addGeneration } = useRecentGenerationsContext()
	const prevStatusRef = useRef(status)

	useEffect(() => {
		const wasRunning = prevStatusRef.current === 'running'
		prevStatusRef.current = status

		if (!wasRunning) return
		if (status !== 'done') return

		// Pending snapshot остался — значит handleGenerate не отработал
		// сам (reload, навигация до завершения, etc). Подхватываем:
		const snapshot = loadPendingSnapshot()
		if (!snapshot || !snapshot.taskId) return

		getTaskStatus(snapshot.taskId)
			.then((s) => {
				if (s.status !== 'completed' || !s.result_url) return
				// TaskStatusResponse теперь содержит и result_urls (все треки),
				// и image_urls (обложки) для audio — пробрасываем в историю.
				saveToHistory({
					resultUrl: s.result_url,
					resultUrls: s.result_urls ?? null,
					imageUrls: s.image_urls ?? null,
					snapshot,
					addGeneration,
				})
				clearPendingSnapshot()
			})
			.catch(() => {
				// Network error — оставляем snapshot, может следующий раз повезёт
			})
	}, [status, addGeneration])

	return null
}
